/**
 * TemplateDetailModal Component
 * 
 * Displays detailed information about a trip template including full itinerary,
 * budget breakdown, and customization options.
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { View, Modal, ScrollView, Image, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, SubHeading, Body, Caption } from '../ui/Text';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import type { TripTemplate } from '@/types/tripTemplates';
import { useUserStore } from '@/state/userStore';
import { SaveTemplateButton } from '@/components/recommendations/SaveTemplateButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import { formatTemplateTagLabel } from '@/utils/templateTags';
import { shareTemplate } from '@/services/templateShare';
import { getTemplateReviewPreview, type TemplateReviewPreview } from '@/services/templateRatings';
import { useToast } from '@/components/ui/ToastProvider';

type TemplateDetailModalProps = {
  visible: boolean;
  template: TripTemplate | null;
  onClose: () => void;
  onDismiss?: () => void;
  onUseTemplate: (template: TripTemplate) => void;
  mode?: 'detail' | 'customization';
  customizationContent?: ReactNode;
};

export function TemplateDetailModal({
  visible,
  template,
  onClose,
  onDismiss,
  onUseTemplate,
  mode = 'detail',
  customizationContent,
}: TemplateDetailModalProps) {
  const theme = useTheme();
  const { showSuccess } = useToast();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [reviewPreview, setReviewPreview] = useState<TemplateReviewPreview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    if (!visible || !template?.id || !firebaseUser?.uid || (template.reviewCount ?? 0) <= 0) {
      setReviewPreview([]);
      return;
    }
    let isMounted = true;
    setReviewsLoading(true);
    getTemplateReviewPreview(template.id, 3)
      .then((reviews) => {
        if (isMounted) {
          setReviewPreview(reviews);
        }
      })
      .catch((error) => {
        console.warn('[TemplateDetailModal] Failed to load template reviews', error);
        if (isMounted) {
          setReviewPreview([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setReviewsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [visible, template?.id, template?.reviewCount, firebaseUser?.uid]);

  if (!template) return null;

  /** Below status bar / notch inside modal — SafeAreaView insets badges + close uniformly. */
  const overlayTop = theme.spacing(2);
  const getDifficultyColor = () => {
    switch (template.difficulty) {
      case 'easy':
        return theme.colors.success;
      case 'moderate':
        return theme.colors.warning;
      case 'adventurous':
        return theme.colors.error;
      default:
        return theme.colors.textSecondary;
    }
  };

  // Helper to determine which city a day belongs to
  const getCityForDay = (dayNumber: number) => {
    if (!template.destinations || template.destinations.length <= 1) return null;
    
    for (const dest of template.destinations) {
      const destStart = new Date(dest.startDate);
      const destEnd = new Date(dest.endDate);
      const daysSinceStart = Math.ceil((destStart.getTime() - new Date(template.destinations[0].startDate).getTime()) / (1000 * 60 * 60 * 24));
      const daysUntilEnd = Math.ceil((destEnd.getTime() - new Date(template.destinations[0].startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      if (dayNumber >= daysSinceStart + 1 && dayNumber <= daysUntilEnd) {
        return dest;
      }
    }
    return null;
  };

  // Check if this day starts a new city
  const isFirstDayOfCity = (dayNumber: number) => {
    const currentCity = getCityForDay(dayNumber);
    if (!currentCity || dayNumber === 1) return currentCity;
    
    const prevCity = getCityForDay(dayNumber - 1);
    return currentCity !== prevCity ? currentCity : null;
  };

  const currencyCode = template.estimatedBudget?.currency ?? 'USD';
  const budgetMinRaw = template.estimatedBudget?.min;
  const budgetMaxRaw = template.estimatedBudget?.max;
  const hasNumericBudgetRange =
    typeof budgetMinRaw === 'number' &&
    typeof budgetMaxRaw === 'number' &&
    !Number.isNaN(budgetMinRaw) &&
    !Number.isNaN(budgetMaxRaw);

  /** User-created drafts may omit amounts; Firebase can store null despite TS typing. */
  const formatBudget = (amount: number | null | undefined) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
      return '—';
    }
    const symbol = currencyCode === 'USD' ? '$' : currencyCode;
    return `${symbol}${amount.toLocaleString()}`;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      flight: 'airplane-outline',
      hotel: 'bed-outline',
      lodging: 'home-outline',
      restaurant: 'restaurant-outline',
      activity: 'bicycle-outline',
      attraction: 'eye-outline',
      transport: 'car-outline',
      shopping: 'cart-outline',
      other: 'ellipsis-horizontal-circle-outline',
    };
    return icons[category] || 'calendar-outline';
  };

  const travelStyleKeySet = new Set((template.travelStyle ?? []).map((s) => String(s).toLowerCase()));
  const topicTags = (template.tags ?? []).filter(
    (tag) => String(tag).trim().length > 0 && !travelStyleKeySet.has(String(tag).trim().toLowerCase()),
  );
  const hasRating =
    typeof template.rating === 'number' &&
    Number.isFinite(template.rating) &&
    (template.reviewCount ?? 0) > 0;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      onDismiss={onDismiss}
    >
      {mode === 'customization' && customizationContent ? (
        customizationContent
      ) : (
      <SafeAreaView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        edges={['top', 'left', 'right']}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: theme.spacing(2) }}
        >
          {/* Hero Image */}
          <View style={{ position: 'relative' }}>
            <View style={{ width: '100%', height: 240, backgroundColor: theme.colors.borderMuted }}>
              {template.coverImage ? (
                <Image
                  source={{ uri: template.coverImage }}
                  style={{ width: '100%', height: '100%' }}
                  resizeMode="cover"
                />
              ) : (
                <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <Ionicons name="image-outline" size={64} color={theme.colors.textSecondary} />
                </View>
              )}
            </View>

            {/* Darken hero bottom so title area reads clearly against varying photos */}
            <LinearGradient
              colors={['transparent', 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0.92)']}
              locations={[0.45, 0.82, 1]}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 112,
              }}
              pointerEvents="none"
            />

            {/* Close Button */}
            <Pressable
              onPress={onClose}
              style={{
                position: 'absolute',
                top: overlayTop,
                right: theme.spacing(2),
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radii.full,
                padding: theme.spacing(1),
                ...theme.shadows.level2,
              }}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>

            {/* Featured Badge */}
            {template.featured && (
              <View
                style={{
                  position: 'absolute',
                  top: overlayTop,
                  left: theme.spacing(2),
                  backgroundColor: theme.colors.primary,
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.75),
                  borderRadius: theme.radii.md,
                  ...theme.shadows.level2,
                }}
              >
                <Caption style={{ color: theme.colors.onPrimary, fontWeight: '700', fontSize: 12 }}>Featured</Caption>
              </View>
            )}
          </View>

          {/* Content — extra top padding separates title from hero edge */}
          <View style={{ paddingHorizontal: theme.spacing(2), paddingTop: theme.spacing(2.5), paddingBottom: theme.spacing(2), gap: theme.spacing(2.5) }}>
            {/* Title and Meta */}
            <View style={{ gap: theme.spacing(1) }}>
              <ThemedText variant="h2" style={{ fontSize: 24 }}>
                {template.title}
              </ThemedText>

              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
                <Body color={theme.colors.textSecondary}>{template.destination}</Body>
              </View>

              {template.shortDescription && (
                <Body color={theme.colors.text}>
                  {template.shortDescription}
                </Body>
              )}

              {template.longDescription && (
                <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                  {template.longDescription}
                </Body>
              )}
            </View>

            {/* Stats Overview */}
            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1.5) }}>
                {/* Duration */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Ionicons name="calendar-outline" size={20} color={theme.colors.text} />
                    <Body>Duration</Body>
                  </View>
                  <Body style={{ fontWeight: '600' }}>
                    {template.duration} {template.duration === 1 ? 'day' : 'days'}
                  </Body>
                </View>

                {/* Difficulty */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Ionicons name="trail-sign-outline" size={20} color={theme.colors.text} />
                    <Body>Difficulty</Body>
                  </View>
                  <Body style={{ fontWeight: '600', color: getDifficultyColor(), textTransform: 'capitalize' }}>
                    {template.difficulty}
                  </Body>
                </View>

                {/* Budget */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Ionicons name="wallet-outline" size={20} color={theme.colors.text} />
                    <Body>Estimated Budget</Body>
                  </View>
                  <Body style={{ fontWeight: '600' }}>
                    {hasNumericBudgetRange
                      ? `${formatBudget(budgetMinRaw)}-${formatBudget(budgetMaxRaw)}`
                      : 'Not set'}
                  </Body>
                </View>
                <Caption color={theme.colors.textSecondary} style={{ marginTop: -theme.spacing(0.5) }}>
                  Estimated per person
                </Caption>

                {/* Traveler Rating */}
                {hasRating && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                      <Ionicons name="star" size={20} color={theme.colors.warning} />
                      <Body>Traveler rating</Body>
                    </View>
                    <Body style={{ fontWeight: '600' }}>
                      {template.rating!.toFixed(1)} ({template.reviewCount} review{template.reviewCount === 1 ? '' : 's'})
                    </Body>
                  </View>
                )}

                {/* Best Months */}
                {template.bestMonths && template.bestMonths.length > 0 && (
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                      <Ionicons name="sunny-outline" size={20} color={theme.colors.text} />
                      <Body>Best Months</Body>
                    </View>
                    <Body style={{ fontWeight: '600', textAlign: 'right', flex: 1, marginLeft: theme.spacing(1) }}>
                      {template.bestMonths.join(', ')}
                    </Body>
                  </View>
                )}
              </View>
            </Card>

            {/* Review Preview */}
            {hasRating && (reviewsLoading || reviewPreview.length > 0) && (
              <View>
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Traveler Reviews</SubHeading>
                <Card variant="outlined">
                  {reviewsLoading ? (
                    <Body color={theme.colors.textSecondary}>Loading reviews...</Body>
                  ) : (
                    <View style={{ gap: theme.spacing(1.5) }}>
                      {reviewPreview.map((review) => (
                        <View key={review.id} style={{ gap: theme.spacing(0.5) }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.35) }}>
                            {[1, 2, 3, 4, 5].map((value) => (
                              <Ionicons
                                key={value}
                                name={value <= review.rating ? 'star' : 'star-outline'}
                                size={14}
                                color={theme.colors.warning}
                              />
                            ))}
                            {review.updatedAt && (
                              <Caption color={theme.colors.textSecondary} style={{ marginLeft: theme.spacing(0.5) }}>
                                {review.updatedAt.toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </Caption>
                            )}
                          </View>
                          <Body color={theme.colors.textSecondary}>{review.review}</Body>
                        </View>
                      ))}
                    </View>
                  )}
                </Card>
              </View>
            )}

            {/* Ticket Reminder */}
            <Card variant="outlined" style={{ backgroundColor: `${theme.colors.primary}12` }}>
              <View style={{ gap: theme.spacing(1), alignItems: 'center' }}>
                <Ionicons name="ticket-outline" size={18} color={theme.colors.primary} />
                <Body color={theme.colors.primary} style={{ textAlign: 'center' }}>
                  Tickets and reservations aren’t included. After you create this trip, you can open each itinerary item to attach your booking confirmations or passes so everything lives in one place.
                </Body>
              </View>
            </Card>

            {/* Budget Breakdown */}
            <View>
              <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Budget Breakdown</SubHeading>
              <Card variant="outlined">
                <View style={{ gap: theme.spacing(1.5) }}>
                  {Object.entries(template.estimatedBudget.breakdown ?? {}).map(([category, amount]) => {
                    const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
                    if (n === 0) return null;
                    const midpoint =
                      hasNumericBudgetRange && budgetMinRaw != null && budgetMaxRaw != null
                        ? (budgetMinRaw + budgetMaxRaw) / 2
                        : 0;
                    const percentage = midpoint > 0 ? Math.min(100, Math.round((n / midpoint) * 100)) : 0;

                    return (
                      <View key={category}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: theme.spacing(0.5) }}>
                          <Caption style={{ textTransform: 'capitalize' }}>{category}</Caption>
                          <Caption style={{ fontWeight: '600' }}>{formatBudget(n)}</Caption>
                        </View>
                        <View
                          style={{
                            height: 6,
                            backgroundColor: theme.colors.borderMuted,
                            borderRadius: theme.radii.sm,
                            overflow: 'hidden',
                          }}
                        >
                          <View
                            style={{
                              height: '100%',
                              width: `${percentage}%`,
                              backgroundColor: theme.colors.primary,
                            }}
                          />
                        </View>
                      </View>
                    );
                  })}
                </View>
              </Card>
            </View>

            {/* Highlights */}
            {template.highlights && template.highlights.length > 0 && (
              <View>
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Highlights</SubHeading>
                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(1) }}>
                    {template.highlights.map((highlight, index) => (
                      <View key={index} style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                        <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                        <Body style={{ flex: 1 }}>{highlight}</Body>
                      </View>
                    ))}
                  </View>
                </Card>
              </View>
            )}

            {/* Itinerary */}
            <View>
              <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Day-by-Day Itinerary</SubHeading>
              <View style={{ gap: theme.spacing(1.5) }}>
                {template.itinerary.map((day) => {
                  const newCity = isFirstDayOfCity(day.day);
                  return (
                    <View key={day.day}>
                      {/* City Transition Header */}
                      {newCity && (
                        <View
                          style={{
                            padding: theme.spacing(2),
                            borderRadius: theme.radii.lg,
                            backgroundColor: theme.colors.primary + '15',
                            borderWidth: 2,
                            borderColor: theme.colors.primary,
                            marginBottom: theme.spacing(1.5),
                          }}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                            <Ionicons name="airplane" size={18} color={theme.colors.primary} />
                            <Body style={{ color: theme.colors.primary, fontWeight: '600' }}>
                              Travel to: {newCity.city}
                            </Body>
                          </View>
                        </View>
                      )}
                      
                      <Card variant="outlined">
                        <Pressable onPress={() => setExpandedDay(expandedDay === day.day ? null : day.day)}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
                            <View style={{ flex: 1, minWidth: 0 }}>
                              <Body style={{ fontWeight: '600' }}>Day {day.day}</Body>
                              <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                                {day.title}
                              </Caption>
                            </View>
                            <Ionicons
                              name={expandedDay === day.day ? 'chevron-up' : 'chevron-down'}
                              size={20}
                              color={theme.colors.textSecondary}
                            />
                          </View>
                        </Pressable>

                    {expandedDay === day.day && (
                      <View style={{ marginTop: theme.spacing(1.5), paddingTop: theme.spacing(1.5), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted, gap: theme.spacing(1.5) }}>
                        {day.activities.map((activity, index) => (
                          <View key={index} style={{ flexDirection: 'row', gap: theme.spacing(1.5), alignItems: 'flex-start' }}>
                            {/* Time */}
                            <View style={{ width: 50, flexShrink: 0 }}>
                              <Caption color={theme.colors.textSecondary}>{activity.time}</Caption>
                            </View>

                            {/* Activity Details */}
                            <View style={{ flex: 1, minWidth: 0 }}>
                              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.25) }}>
                                <Ionicons
                                  name={getCategoryIcon(activity.category) as any}
                                  size={14}
                                  color={theme.colors.primary}
                                  style={{ marginTop: 2, flexShrink: 0 }}
                                />
                                <Body style={{ fontWeight: '600', flex: 1, flexShrink: 1 }}>{activity.title}</Body>
                              </View>

                              {(activity.venueName || activity.location) && (
                                <Caption color={theme.colors.textSecondary}>{activity.venueName || activity.location}</Caption>
                              )}

                              {activity.description && (
                                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                                  {activity.description}
                                </Caption>
                              )}

                              {typeof activity.estimatedCost === 'number' && !Number.isNaN(activity.estimatedCost) && (
                                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                                  {activity.estimatedCost === 0 ? 'Free' : `Est. ${formatBudget(activity.estimatedCost)}`}
                                </Caption>
                              )}

                              {activity.notes && (
                                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                                  {activity.notes}
                                </Caption>
                              )}
                            </View>
                          </View>
                        ))}
                      </View>
                    )}
                      </Card>
                    </View>
                  );
                })}
              </View>
            </View>

            {/* Travel Styles */}
            {(template.travelStyle?.length ?? 0) > 0 && (
              <View>
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Travel Styles</SubHeading>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                  {(template.travelStyle ?? []).map((style) => (
                    <View
                      key={style}
                      style={{
                        paddingHorizontal: theme.spacing(1.5),
                        paddingVertical: theme.spacing(0.75),
                        borderRadius: theme.radii.md,
                        backgroundColor: `${theme.colors.primary}15`,
                        borderWidth: 1,
                        borderColor: theme.colors.primary,
                      }}
                    >
                      <Caption color={theme.colors.primary} style={{ textTransform: 'capitalize', fontWeight: '600' }}>
                        {style}
                      </Caption>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Topic tags (collections, campaigns — e.g. World Cup) */}
            {topicTags.length > 0 && (
              <View>
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Topics</SubHeading>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                  {topicTags.map((tag) => (
                    <View
                      key={tag}
                      style={{
                        paddingHorizontal: theme.spacing(1.5),
                        paddingVertical: theme.spacing(0.75),
                        borderRadius: theme.radii.md,
                        backgroundColor: `${theme.colors.primary}18`,
                        borderWidth: 1,
                        borderColor: `${theme.colors.primary}45`,
                      }}
                    >
                      <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                        {formatTemplateTagLabel(tag)}
                      </Caption>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Usage Count */}
            {template.usageCount > 0 && (
              <View style={{ alignItems: 'center', paddingTop: theme.spacing(1) }}>
                <Caption color={theme.colors.textSecondary}>
                  {template.usageCount} {template.usageCount === 1 ? 'traveler has' : 'travelers have'} used this
                  template
                </Caption>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Bottom Action Bar */}
        <SafeAreaView edges={['bottom']} style={{ backgroundColor: theme.colors.surface }}>
          <View
            style={{
              paddingHorizontal: theme.spacing(2),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              backgroundColor: theme.colors.surface,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              {firebaseUser?.uid ? (
                <SaveTemplateButton userId={firebaseUser.uid} template={template} />
              ) : (
                <View style={{ width: 32 }} />
              )}
              <Button
                label="Share"
                variant="secondary"
                onPress={() => {
                  void shareTemplate(template, {
                    source: 'template_detail',
                    onCopied: () => showSuccess('Template link copied'),
                  });
                }}
                icon={<Ionicons name="share-outline" size={18} color={theme.colors.text} />}
              />
              <Button label="Use This Template" onPress={() => onUseTemplate(template)} style={{ flex: 1 }} />
            </View>
          </View>
        </SafeAreaView>
      </SafeAreaView>
      )}
    </Modal>
  );
}

